import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { EASE, Reveal } from '../components/motion.tsx';
import { Avatar, Button, Callout, Dialog, Empty, Field, Panel, Skeleton, StatusChip, Tabs, useToast } from '../components/ui.tsx';
import { ApiError, get, photoUrl, post } from '../lib/api.ts';
import { date, idr, todayIn } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';
import { PersonEditDialog, useInvite, useRemove, type ContractRef, type EditKind } from '../components/PersonEdit.tsx';
import { PersonComponents } from '../components/PersonComponents.tsx';
import { PersonSavings } from '../components/PersonSavings.tsx';
import { ResignationPanel } from '../components/Resignation.tsx';
import { GxMailRow, SetupLinkDialog, type GxMailView } from '../components/GxMail.tsx';
import { ChangePasswordDialog } from '../components/ChangePassword.tsx';
import { DocumentsPanel, RecordsPanel, SignInPanel, type Account } from '../components/PersonFiles.tsx';
import { PersonPayslips } from '../components/PersonPayslips.tsx';
import type { PersonDocumentKind } from '../lib/documents.ts';

interface Profile {
  id: string;
  employeeNo: string | null;
  status: string;
  name: string;
  photoFileId: string | null;
  removedAt: string | null;
  autoStart: boolean;
  givenName: string;
  familyName: string | null;
  preferredName: string | null;
  gender: string | null;
  birthDate: string | null;
  birthPlace: string | null;
  religion: Ref | null;
  maritalStatus: Ref | null;
  educationLevel: Ref | null;
  childrenCount: number | null;
  spouseName: string | null;
  university: string | null;
  highSchool: string | null;
  training: string | null;
  uniformSize: string | null;
  company: { id: string; name: string; isPrimary: boolean } | null;
  office: { id: string; name: string; timeZone: string } | null;
  employment: null | {
    type: string;
    department: { id: string; name: string } | null;
    division: { id: string; name: string } | null;
    section: { id: string; name: string } | null;
    jobPosition: { id: string; name: string } | null;
    jobLevel: { id: string; name: string } | null;
    hiredOn: string | null;
    firstContractOn: string | null;
    startedOn: string | null;
    contractEndsOn: string | null;
    resignedOn: string | null;
    attendanceExempt: boolean;
    holidayAllowanceEligible: boolean;
    mandatorySaving: boolean;
  };
  contact: null | { workEmail: string | null; personalEmail: string | null; mobilePhone: string | null; altMobilePhone: string | null; currentAddress: string | null; currentCity: string | null; residentAddress: string | null; residentCity: string | null };
  identity: null | { ptkpCode: string; nationalId: string | null; npwp: string | null; bpjsEmployment: string | null; bpjsHealth: string | null };
  /** enteredAt/By: who saved the account in Hearth; null for accounts carried over from GX Employee. */
  bank: null | { bank: { code: string | null; name: string } | null; accountNo: string | null; holderName: string | null; isOwnAccount: boolean; enteredAt: string | null; enteredBy: string | null };
  documents: Record<PersonDocumentKind, boolean>;
  family: { id: string; relation: string; name: string | null; phone: string | null; city: string | null; deceased: boolean }[];
  emergency: { id: string; name: string; relationship: string; phone: string; email: string | null; city: string | null }[];
  contracts: { id: string; type: string; secondCompany: boolean; startsOn: string; endsOn: string | null; changesSalary: boolean; hasDocument: boolean; applied: boolean }[];
  records: { id: string; kind: string; description: string | null; fromDate: string | null; toDate: string | null; hasDocument: boolean; createdAt: string }[];
  /** linkByEmail: set-up links can be e-mailed; otherwise HR hands them over. */
  account: Account | null;
  gxMail: GxMailView;
  salaryHistory: { effectiveFrom: string; basicSalary: string; reason: string | null }[];
}

type Ref = { id: string; name: string };
type Tab = 'overview' | 'employment' | 'people' | 'documents' | 'pay';

export function MyProfile() {
  const me = useMe().data!;
  if (!me.employee) return <Empty title="No employee profile">Your account isn’t linked to an employee record.</Empty>;
  return <ProfileView id={me.employee.id} self />;
}

export function Person() {
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useMe().data!;
  return <ProfileView id={id} self={me.employee?.id === id} />;
}

function addDaysIso(iso: string, n: number) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function tenure(from: string | null | undefined) {
  if (!from) return null;
  const months = Math.max(0, Math.floor((Date.now() - Date.parse(from)) / (30.44 * 86_400_000)));
  const y = Math.floor(months / 12);
  const m = months % 12;
  return y ? `${y}y ${m}m` : `${m} months`;
}

function ProfileView({ id, self }: { id: string; self: boolean }) {
  const me = useMe().data!;
  const q = useQuery({ queryKey: ['person', id], queryFn: () => get<Profile>(`/api/people/${id}`) });
  const [tab, setTab] = useState<Tab>('overview');
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealed, setRevealed] = useState<Pick<Profile, 'identity' | 'bank'> | null>(null);
  const [edit, setEdit] = useState<EditKind | null>(null);
  const [asking, setAsking] = useState<'personal' | 'contact' | null>(null);
  const [documentFor, setDocumentFor] = useState<ContractRef | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [starting, setStarting] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const mine = useQuery({ queryKey: ['my-change-requests'], queryFn: () => get<{ id: string; section: string; status: string }[]>('/api/people/requests/profile/mine'), enabled: self });
  const waiting = (mine.data ?? []).filter((r) => r.status === 'pending').map((r) => r.section);
  const remove = useRemove(id);
  const [setupLink, setSetupLink] = useState<string | null>(null);
  const invite = useInvite(id, setSetupLink);
  const p = q.data;
  // A cancelled onboarding is kept read-only, for the audit trail.
  const canEdit = !self && me.access.employeeEdit && !p?.removedAt;
  const canEditSensitive = canEdit && me.access.sensitive;
  const editBtn = (kind: EditKind, label = 'Edit') => <Button size="sm" variant="ghost" onClick={() => setEdit(kind)}>{label}</Button>;

  if (q.isError) return <Empty title="Not available">{q.error instanceof ApiError ? q.error.message : 'This profile could not be loaded.'}</Empty>;

  const identity = revealed?.identity ?? p?.identity;
  const bank = revealed?.bank ?? p?.bank;
  const canReveal = self || me.access.sensitive;
  // Signed contracts: the employee's own, or HR who may see sensitive records (the API checks scope and audits each open).
  const canOpenDocuments = self || me.access.sensitive;

  return (
    <div>
      <header className="profile-head">
        <div className="grid-field" />
        {p ? <Avatar name={p.name} size={112} photo={p.photoFileId ? photoUrl(p.id, { size: 'lg', version: p.photoFileId }) : null} /> : <Skeleton w={112} h={112} r={56} />}
        <div>
          <motion.div className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {p ? `${p.employeeNo ?? 'No number yet'} · ${p.company?.name ?? ''}` : '…'}
          </motion.div>
          <motion.h1 className="profile-head__name" initial={{ opacity: 0, y: 18, filter: 'blur(8px)' }} animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }} transition={{ duration: 0.9, ease: EASE }}>
            {p ? (
              <>
                {p.givenName} {p.familyName ? <em>{p.familyName}</em> : null}
              </>
            ) : (
              <Skeleton h={64} w={420} />
            )}
          </motion.h1>
          {p ? (
            <div className="profile-facts">
              <span>
                <Icon name="org" />
                {p.employment?.jobPosition?.name ?? '—'}
                {p.employment?.jobLevel ? <span className="faint">· {p.employment.jobLevel.name}</span> : null}
              </span>
              <span>
                <Icon name="people" />
                {p.employment?.division?.name ?? p.employment?.department?.name ?? '—'}
              </span>
              <span>
                <Icon name="pin" />
                {p.office?.name}
              </span>
              <span>
                <Icon name="clock" />
                {tenure(p.employment?.firstContractOn) ?? '—'} with GX
              </span>
            </div>
          ) : null}
        </div>
        <div className="row row--wrap profile-head__actions">
          {p?.removedAt ? <span className="chip chip--bad">Onboarding cancelled</span> : p ? <StatusChip status={p.status} /> : null}
          {p && !p.removedAt && p.status === 'onboarding' ? (
            <span className="faint" style={{ fontSize: 'var(--step--2)' }}>
              {p.autoStart ? `Starts by itself on ${date(p.employment?.startedOn)}` : 'Waiting to be marked as started'}
            </span>
          ) : null}
          {p && !p.removedAt && p.status === 'onboarding' && !self && me.access.recruit ? (
            <>
              <Button size="sm" icon="check" onClick={() => setStarting(true)}>
                Mark as started
              </Button>
              <Button size="sm" variant="ghost" icon="x" onClick={() => setCancelling(true)}>
                Cancel onboarding
              </Button>
            </>
          ) : null}
          {p && !p.removedAt && canEdit && p.account && (!p.account.lastLoginAt || !p.account.linkByEmail) ? (
            <Button size="sm" icon="key" loading={invite.isPending} onClick={() => invite.mutate()}>
              {p.account.linkByEmail ? 'Send set-up link' : 'Set-up link'}
            </Button>
          ) : null}
          {p && self ? (
            <Button size="sm" variant="ghost" icon="key" onClick={() => setPasswordOpen(true)}>
              Change password
            </Button>
          ) : null}
          {p && me.access.attendance ? (
            <Link to="/attendance/$employeeId" params={{ employeeId: p.id }}>
              <Button size="sm" icon="clock">
                Timesheet
              </Button>
            </Link>
          ) : null}
        </div>
      </header>

      {p?.removedAt ? (
        <div style={{ marginBottom: 'var(--s-5)' }}>
          <Callout tone="bad" title={`Onboarding cancelled on ${date(p.removedAt)}`}>
            This record is kept for the audit trail only. It no longer appears in the directory, and the account can't sign in.
          </Callout>
        </div>
      ) : null}

      <Tabs
        idPrefix="profile"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'employment', label: 'Employment', count: p?.contracts.length },
          { id: 'people', label: 'Family & emergency', count: p ? p.family.length + p.emergency.length : undefined },
          { id: 'documents', label: 'Documents & records', count: p ? p.records.length : undefined },
          ...((self || me.access.payroll) ? [{ id: 'pay' as const, label: 'Pay & bank' }] : []),
        ]}
      />

      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.35, ease: EASE }} style={{ marginTop: 'var(--s-6)' }}>
          {!p ? (
            <Skeleton h={240} />
          ) : tab === 'overview' ? (
            <Reveal className="grid-12">
              <Reveal.Item className="col-7">
                <Panel
                  eyebrow="Personal"
                  title="About"
                  action={
                    canEdit ? (
                      <div className="row" style={{ gap: 4 }}>{editBtn('personal', 'Personal')}{editBtn('contact', 'Contact')}</div>
                    ) : self ? (
                      <div className="row" style={{ gap: 4 }}>
                        <Button size="sm" variant="ghost" disabled={waiting.includes('personal')} onClick={() => setAsking('personal')}>Change personal</Button>
                        <Button size="sm" variant="ghost" disabled={waiting.includes('contact')} onClick={() => setAsking('contact')}>Change contact</Button>
                      </div>
                    ) : undefined
                  }
                >
                  {waiting.length ? <Callout tone="info">Your {waiting.join(' and ')} change is waiting for Human Capital.</Callout> : null}
                  <dl className="dl">
                    <dt>Full name</dt>
                    <dd>
                      {p.name} {p.preferredName ? <span className="faint">· called {p.preferredName}</span> : null}
                    </dd>
                    <dt>Born</dt>
                    <dd>
                      {date(p.birthDate)} {p.birthPlace ? <span className="faint">· {p.birthPlace}</span> : null}
                    </dd>
                    <dt>Gender</dt>
                    <dd style={{ textTransform: 'capitalize' }}>{p.gender ?? '—'}</dd>
                    <dt>Religion</dt>
                    <dd>{p.religion?.name ?? '—'}</dd>
                    <dt>Marital status</dt>
                    <dd>
                      {p.maritalStatus?.name ?? '—'}
                      {p.spouseName ? <span className="faint"> · {p.spouseName}</span> : null}
                      {p.childrenCount ? <span className="faint"> · {p.childrenCount} {p.childrenCount === 1 ? 'child' : 'children'}</span> : null}
                    </dd>
                    <dt>Education</dt>
                    <dd>
                      {p.educationLevel?.name ?? '—'}
                      {p.university || p.highSchool ? <span className="faint"> · {p.university ?? p.highSchool}</span> : null}
                    </dd>
                    {p.training ? (
                      <>
                        <dt>Training</dt>
                        <dd>{p.training}</dd>
                      </>
                    ) : null}
                    <dt>Uniform size</dt>
                    <dd>{p.uniformSize ?? '—'}</dd>
                    <dt>Work email</dt>
                    <dd>{p.contact?.workEmail ?? p.account?.email ?? '—'}</dd>
                    <GxMailRow employeeId={p.id} name={p.name} signIn={p.account?.email ?? null} view={p.gxMail} canEdit={canEdit} hasLeft={p.status === 'resigned' || p.status === 'terminated' || !!p.removedAt} />
                    <dt>Private email</dt>
                    <dd>{p.contact?.personalEmail ?? '—'}</dd>
                    <dt>Mobile</dt>
                    <dd className="mono">
                      {p.contact?.mobilePhone ?? '—'}
                      {p.contact?.altMobilePhone ? <span className="faint"> · {p.contact.altMobilePhone}</span> : null}
                    </dd>
                    <dt>Lives in</dt>
                    <dd>{[p.contact?.currentAddress, p.contact?.currentCity].filter(Boolean).join(', ') || '—'}</dd>
                    <dt>KTP address</dt>
                    <dd>{[p.contact?.residentAddress, p.contact?.residentCity].filter(Boolean).join(', ') || '—'}</dd>
                  </dl>
                </Panel>
              </Reveal.Item>
              <Reveal.Item className="col-5">
                <Panel
                  eyebrow="Restricted"
                  title="Identity"
                  action={
                    canReveal && !revealed ? (
                      <div className="row" style={{ gap: 4 }}>
                        {canEditSensitive ? editBtn('identity', 'Update') : null}
                        <Button size="sm" icon="eye" onClick={() => setRevealOpen(true)}>
                          Reveal
                        </Button>
                      </div>
                    ) : revealed ? (
                      <Button size="sm" variant="ghost" icon="lock" onClick={() => setRevealed(null)}>
                        Hide
                      </Button>
                    ) : null
                  }
                >
                  <dl className="dl">
                    <dt>NIK</dt>
                    <dd className="secret">{identity?.nationalId ?? '—'}</dd>
                    <dt>NPWP</dt>
                    <dd className="secret">{identity?.npwp ?? '—'}</dd>
                    <dt>BPJS Ketenagakerjaan</dt>
                    <dd className="secret">{identity?.bpjsEmployment ?? '—'}</dd>
                    <dt>BPJS Kesehatan</dt>
                    <dd className="secret">{identity?.bpjsHealth ?? '—'}</dd>
                    <dt>Tax status (PTKP)</dt>
                    <dd className="mono">{identity?.ptkpCode ?? '—'}</dd>
                  </dl>
                  {revealed ? (
                    <p className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 12 }}>
                      Shown to you just now. This view was recorded in the audit trail.
                    </p>
                  ) : null}
                </Panel>
              </Reveal.Item>
            </Reveal>
          ) : tab === 'documents' ? (
            <Reveal className="grid-12">
              <Reveal.Item className={canEdit ? 'col-7' : 'col-12'}>
                <DocumentsPanel id={id} documents={p.documents} hasBank={!!p.bank} canOpen={canOpenDocuments} canUpload={canEditSensitive} />
              </Reveal.Item>
              {canEdit ? (
                <Reveal.Item className="col-5">
                  <SignInPanel id={id} name={p.givenName} account={p.account} hasLeft={p.status === 'resigned' || p.status === 'terminated'} canEdit={canEdit} />
                </Reveal.Item>
              ) : null}
              <Reveal.Item className="col-12">
                <RecordsPanel id={id} records={p.records} canEdit={canEdit} canOpen={canOpenDocuments} />
              </Reveal.Item>
            </Reveal>
          ) : tab === 'employment' ? (
            <Reveal className="grid-12">
              <Reveal.Item className="col-6">
                <Panel eyebrow="Current" title="Employment" action={canEdit ? editBtn('employment') : undefined}>
                  <dl className="dl">
                    <dt>Type</dt>
                    <dd style={{ textTransform: 'capitalize' }}>{p.employment?.type}</dd>
                    <dt>Department</dt>
                    <dd>{p.employment?.department?.name ?? '—'}</dd>
                    <dt>Division</dt>
                    <dd>{p.employment?.division?.name ?? '—'}</dd>
                    <dt>Section</dt>
                    <dd>{p.employment?.section?.name ?? '—'}</dd>
                    <dt>Position</dt>
                    <dd>{p.employment?.jobPosition?.name ?? '—'}{p.employment?.jobLevel ? <span className="faint"> · {p.employment.jobLevel.name}</span> : null}</dd>
                    <dt>First contract</dt>
                    <dd>{date(p.employment?.firstContractOn)}</dd>
                    <dt>Contract ends</dt>
                    <dd>{date(p.employment?.contractEndsOn)}</dd>
                    <dt>Attendance</dt>
                    <dd>{p.employment?.attendanceExempt ? 'Exempt (never counted absent)' : 'Tracked'}</dd>
                    <dt>THR</dt>
                    <dd>{p.employment?.holidayAllowanceEligible ? 'Eligible' : 'Not yet eligible'}</dd>
                    <dt>Cooperative</dt>
                    <dd>{p.employment?.mandatorySaving ? 'Mandatory saving on' : 'No mandatory saving'}</dd>
                  </dl>
                </Panel>
              </Reveal.Item>
              <Reveal.Item className="col-6">
                <Panel eyebrow="History" title="Contracts" action={canEdit ? editBtn('contract', p.employment?.contractEndsOn ? 'Extend' : 'New contract') : undefined}>
                  {p.contracts.length ? (
                    <ol className="list-plain">
                      {p.contracts.map((c, i) => (
                        <li key={c.id} className="mini-row" style={{ alignItems: 'start' }}>
                          <span className="mono faint" style={{ width: 22 }}>
                            {String(p.contracts.length - i).padStart(2, '0')}
                          </span>
                          <div>
                            <div style={{ textTransform: 'capitalize' }}>
                              {c.type}
                              {c.secondCompany ? <span className="chip chip--info" style={{ marginLeft: 8 }}>Second company</span> : null}
                              {c.applied ? null : c.startsOn >= addDaysIso(todayIn(p.office?.timeZone), -7) ? (
                                <span className="chip chip--info" style={{ marginLeft: 8 }} title="Its changes take effect by themselves on the start date">
                                  {c.startsOn > todayIn(p.office?.timeZone) ? `Applies on ${date(c.startsOn, { year: false })}` : 'Applies tonight'}
                                </span>
                              ) : (
                                <span className="chip chip--wait" style={{ marginLeft: 8 }} title="Its start date passed without its changes being applied (legacy applied a contract only on that exact day). Check the employment details.">
                                  Not applied
                                </span>
                              )}
                            </div>
                            <div className="faint mono" style={{ fontSize: 11 }}>
                              {date(c.startsOn)} → {c.endsOn ? date(c.endsOn) : 'open-ended'}
                            </div>
                          </div>
                          <div className="mini-row__aside row row--wrap" style={{ gap: 4, justifyContent: 'flex-end' }}>
                            {c.hasDocument && canOpenDocuments ? (
                              <a className="btn btn--sm btn--ghost" href={`/api/people/${id}/contracts/${c.id}/document`} target="_blank" rel="noopener">
                                <Icon name="file" />
                                Open document
                              </a>
                            ) : c.hasDocument ? (
                              <span className="chip chip--plain">Document on file</span>
                            ) : null}
                            {canEdit ? (
                              <Button size="sm" variant="ghost" icon={c.hasDocument ? 'refresh' : 'plus'} onClick={() => setDocumentFor(c)}>
                                {c.hasDocument ? 'Replace' : 'Upload'}
                              </Button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="faint">No contracts on file.</p>
                  )}
                </Panel>
              </Reveal.Item>
              {self || me.access.peopleAll ? (
                <Reveal.Item className="col-6">
                  <ResignationPanel employeeId={id} self={self} active={p.status === 'active' || p.status === 'onboarding'} />
                </Reveal.Item>
              ) : null}
            </Reveal>
          ) : tab === 'people' ? (
            <Reveal className="grid-12">
              <Reveal.Item className="col-6">
                <Panel eyebrow="In case of emergency" title="Emergency contacts" action={canEdit ? editBtn('emergency', 'Add') : undefined}>
                  {p.emergency.length ? (
                    <ul className="list-plain">
                      {p.emergency.map((e) => (
                        <li key={e.id} className="mini-row">
                          <Avatar name={e.name} size={30} />
                          <div>
                            <div>{e.name}</div>
                            <div className="faint" style={{ fontSize: 'var(--step--2)' }}>
                              {e.relationship}
                            </div>
                          </div>
                          <a className="mini-row__aside link" href={`tel:${e.phone.replace(/\s/g, '')}`}>
                            {e.phone}
                          </a>
                          {canEdit ? <Button size="sm" variant="ghost" iconOnly icon="x" aria-label={`Remove ${e.name}`} onClick={() => remove.mutate(`emergency/${e.id}`)} /> : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="faint">None recorded.</p>
                  )}
                </Panel>
              </Reveal.Item>
              <Reveal.Item className="col-6">
                <Panel eyebrow="Family" title="Family members" action={canEdit ? editBtn('family', 'Add') : undefined}>
                  {p.family.length ? (
                    <ul className="list-plain">
                      {p.family.map((f) => (
                        <li key={f.id} className="mini-row">
                          <span className="chip chip--plain">{f.relation}</span>
                          {f.name}
                          {f.deceased ? <span className="faint">(deceased)</span> : null}
                          <span className="mini-row__aside">{f.city}</span>
                          {canEdit ? <Button size="sm" variant="ghost" iconOnly icon="x" aria-label={`Remove ${f.name ?? f.relation}`} onClick={() => remove.mutate(`family/${f.id}`)} /> : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="faint">None recorded.</p>
                  )}
                </Panel>
              </Reveal.Item>
            </Reveal>
          ) : (
            <Reveal className="grid-12">
              <Reveal.Item className="col-5">
                <Panel
                  eyebrow="Salary"
                  title="Basic salary history"
                  action={
                    !self && me.access.salaryUpdate ? (
                      editBtn('salary', 'Change')
                    ) : self ? (
                      <Link to="/payroll/simulate" search={{ employeeId: id }} className="btn btn--ghost btn--sm">
                        <Icon name="formula" />
                        Simulate my salary
                      </Link>
                    ) : undefined
                  }
                >
                  {p.salaryHistory.length ? (
                    <ul className="list-plain">
                      {p.salaryHistory.map((s) => (
                        <li key={s.effectiveFrom} className="mini-row">
                          <span className="mono faint">{date(s.effectiveFrom)}</span>
                          <span className="faint">{s.reason}</span>
                          <span className="mini-row__aside num" style={{ color: 'var(--ink-1)', fontSize: 13 }}>
                            {idr(s.basicSalary)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="faint">No salary on file.</p>
                  )}
                </Panel>
              </Reveal.Item>
              <Reveal.Item className="col-7">
                <Panel
                  eyebrow="Transfers"
                  title="Bank account"
                  action={
                    <div className="row" style={{ gap: 4 }}>
                      {canEditSensitive ? editBtn('bank', 'Change account') : null}
                      {canReveal && !revealed ? (
                        <Button size="sm" icon="eye" onClick={() => setRevealOpen(true)}>
                          Reveal
                        </Button>
                      ) : null}
                    </div>
                  }
                >
                  {bank ? (
                    <dl className="dl">
                      <dt>Bank</dt>
                      <dd>{bank.bank?.name ?? <span style={{ color: 'var(--rose)' }}>Not set — payroll export will block</span>}</dd>
                      <dt>Account</dt>
                      <dd className="secret">{bank.accountNo ?? '—'}</dd>
                      <dt>Holder</dt>
                      <dd>{bank.holderName}</dd>
                      <dt>Own account</dt>
                      <dd>{bank.isOwnAccount ? 'Yes' : 'No — paid to another holder'}</dd>
                      <dt>Entered by</dt>
                      <dd title="Who typed this account into Hearth. Nobody checks it with the bank: keep the bank book under Documents.">
                        {bank.enteredAt ? (
                          <>
                            {bank.enteredBy ?? 'HR'} <span className="faint">· {date(bank.enteredAt)}</span>
                          </>
                        ) : (
                          <span className="faint">Carried over from GX Employee</span>
                        )}
                      </dd>
                      <dt>Bank book</dt>
                      <dd>
                        {p.documents.bank_book ? (
                          canOpenDocuments ? (
                            <a className="link" href={`/api/people/${id}/documents/bank_book`} target="_blank" rel="noopener">
                              On file
                            </a>
                          ) : (
                            'On file'
                          )
                        ) : (
                          <span className="faint">Not on file</span>
                        )}
                      </dd>
                    </dl>
                  ) : (
                    <Callout tone="bad">No bank account. This person cannot be paid until one is added.</Callout>
                  )}
                </Panel>
              </Reveal.Item>
              <Reveal.Item className="col-5">
                <PersonSavings employeeId={id} name={p.givenName} self={self} />
              </Reveal.Item>
              <Reveal.Item className="col-7">
                <PersonComponents employeeId={id} name={p.givenName} canEdit={!self && me.access.salaryUpdate} canSimulate={me.access.payroll} />
              </Reveal.Item>
              {!self && me.access.payroll ? (
                <Reveal.Item className="col-12">
                  <PersonPayslips employeeId={id} name={p.givenName} />
                </Reveal.Item>
              ) : null}
            </Reveal>
          )}
        </motion.div>
      </AnimatePresence>
      {edit && p ? <PersonEditDialog kind={edit} id={id} p={p} onClose={() => setEdit(null)} /> : null}
      {asking && p ? <PersonEditDialog kind={asking} id={id} p={p} request onClose={() => setAsking(null)} /> : null}
      {documentFor && p ? <PersonEditDialog kind="contract" id={id} p={p} contract={documentFor} onClose={() => setDocumentFor(null)} /> : null}
      <SetupLinkDialog link={setupLink} name={p?.givenName ?? ''} onClose={() => setSetupLink(null)} />
      {self ? <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} /> : null}
      {starting && p ? <StartDialog id={id} name={p.name} planned={p.employment?.startedOn ?? null} timeZone={p.office?.timeZone} auto={p.autoStart} onClose={() => setStarting(false)} /> : null}
      {cancelling && p ? <CancelOnboardingDialog id={id} name={p.name} onClose={() => setCancelling(false)} /> : null}
      <RevealDialog open={revealOpen} onClose={() => setRevealOpen(false)} id={id} self={self} onRevealed={setRevealed} name={p?.givenName ?? ''} />
    </div>
  );
}

/** Onboarding → active from the day they actually started (today or earlier). */
function StartDialog({ id, name, planned, timeZone, auto, onClose }: { id: string; name: string; planned: string | null; timeZone?: string; auto: boolean; onClose: () => void }) {
  const today = todayIn(timeZone);
  const [startedOn, setStartedOn] = useState(planned && planned <= today ? planned : today);
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => post(`/api/people/${id}/start`, { startedOn }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', id] });
      void qc.invalidateQueries({ queryKey: ['people'] });
      toast({ title: `${name} is now active` });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow="Onboarding"
      title={<>Mark {name} as started</>}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Not yet
          </Button>
          <Button variant="primary" icon="check" loading={m.isPending} disabled={!startedOn || startedOn > today} onClick={() => m.mutate()}>
            Mark as started
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">
          They become active: counted in attendance and payroll from this date, and able to sign in.
          {planned ? ` Planned start: ${date(planned)}.` : ''}
          {auto && planned && planned > today ? ' Hearth would start them by itself on that day; use this only if they started earlier.' : ''}
        </p>
        <Field label="Started on" hint="Today or an earlier date.">
          {(a) => <input {...a} className="input" type="date" max={today} value={startedOn} onChange={(e) => setStartedOn(e.target.value)} />}
        </Field>
        {m.error ? <Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Something went wrong.'}</Callout> : null}
      </div>
    </Dialog>
  );
}

/** Withdraws a hire who never started; the API refuses once there is attendance or a payslip. */
function CancelOnboardingDialog({ id, name, onClose }: { id: string; name: string; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const m = useMutation({
    mutationFn: () => post<{ onMachines: number }>(`/api/people/${id}/cancel-onboarding`, { reason }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person', id] });
      toast({ title: 'Onboarding cancelled', body: r.onMachines ? `${name} is still on ${r.onMachines} attendance machine${r.onMachines === 1 ? '' : 's'}: remove them under Devices.` : undefined });
      onClose();
      void navigate({ to: '/people' });
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow="Onboarding"
      title={<>Cancel onboarding for {name}?</>}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Keep
          </Button>
          <Button variant="danger" icon="x" loading={m.isPending} disabled={reason.trim().length < 3} onClick={() => m.mutate()}>
            Cancel onboarding
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">For a hire who will not start, or a registration made in error. The record leaves the directory but is kept for the audit trail; the account and its set-up link stop working. The e-mail and NIK can be used for a new registration. The employee number is not given out again.</p>
        <Field label="Reason" hint="Kept in the audit trail, e.g. “declined the offer” or “test registration”.">
          {(a) => <textarea {...a} className="textarea" value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        {m.error ? <Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Something went wrong.'}</Callout> : null}
      </div>
    </Dialog>
  );
}

function RevealDialog({ open, onClose, id, self, onRevealed, name }: { open: boolean; onClose: () => void; id: string; self: boolean; onRevealed: (v: Pick<Profile, 'identity' | 'bank'>) => void; name: string }) {
  const [reason, setReason] = useState('');
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => post<Pick<Profile, 'identity' | 'bank'>>(`/api/people/${id}/reveal`, { reason: self ? 'self' : reason }),
    onSuccess: (r) => {
      onRevealed(r);
      onClose();
      setReason('');
      if (!self) toast({ title: 'Details revealed', body: 'Recorded in the audit trail with your reason.' });
    },
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Restricted information"
      title={self ? <>Show your details</> : <>Why do you need <em className="serif-italic">{name}’s</em> details?</>}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" icon="eye" loading={m.isPending} disabled={!self && reason.trim().length < 5} onClick={() => m.mutate()}>
            Reveal
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">NIK, NPWP, BPJS and bank numbers are encrypted. Each reveal is logged with who, when and why.</p>
        {!self ? (
          <Field label="Reason" hint="For example: “BPJS registration for October intake”.">
            {(p) => <textarea {...p} className="textarea" value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
        ) : null}
        {m.error ? <Callout tone="bad">{(m.error as Error).message}</Callout> : null}
      </div>
    </Dialog>
  );
}
